import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Search, ArrowLeft, Database, HardDrive, Clock, Loader2, RefreshCw,
  AlertTriangle, Lightbulb, Trash2, Archive, Eye, TrendingDown
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useSnowflakeCredentials } from '../components/SnowflakeConnect';
import SnowflakeConnect from '../components/SnowflakeConnect';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import axios from 'axios';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StorageDetective = () => {
  const [loading, setLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [storageData, setStorageData] = useState(null);
  const { credentials, isConnected, updateCredentials } = useSnowflakeCredentials();
  const { shouldShowData } = useDemoData();

  const fetchData = async () => {
    if (!isConnected) {
      if (shouldShowData) {
        setStorageData(getMockData());
      }
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/snowflake/advanced/storage-detective`, {
        credentials
      });
      setStorageData(response.data);
      toast.success('Storage analysis complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze storage');
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    current_storage: {
      total_tb: 2.34,
      stage_tb: 0.12,
      failsafe_tb: 0.45
    },
    storage_trend: [
      { USAGE_DATE: '2026-02-01', STORAGE_TB: 1.8, FAILSAFE_TB: 0.35 },
      { USAGE_DATE: '2026-02-08', STORAGE_TB: 1.95, FAILSAFE_TB: 0.38 },
      { USAGE_DATE: '2026-02-15', STORAGE_TB: 2.1, FAILSAFE_TB: 0.41 },
      { USAGE_DATE: '2026-02-22', STORAGE_TB: 2.25, FAILSAFE_TB: 0.43 },
      { USAGE_DATE: '2026-03-01', STORAGE_TB: 2.34, FAILSAFE_TB: 0.45 },
    ],
    top_tables: [
      { DATABASE_NAME: 'ANALYTICS', SCHEMA_NAME: 'PUBLIC', TABLE_NAME: 'FACT_EVENTS', ACTIVE_GB: 456, TIME_TRAVEL_GB: 234, FAILSAFE_GB: 123, TOTAL_GB: 813 },
      { DATABASE_NAME: 'ANALYTICS', SCHEMA_NAME: 'PUBLIC', TABLE_NAME: 'FACT_SALES', ACTIVE_GB: 234, TIME_TRAVEL_GB: 156, FAILSAFE_GB: 89, TOTAL_GB: 479 },
      { DATABASE_NAME: 'RAW', SCHEMA_NAME: 'INGESTION', TABLE_NAME: 'RAW_LOGS', ACTIVE_GB: 345, TIME_TRAVEL_GB: 45, FAILSAFE_GB: 67, TOTAL_GB: 457 },
      { DATABASE_NAME: 'ML', SCHEMA_NAME: 'FEATURES', TABLE_NAME: 'TRAINING_DATA', ACTIVE_GB: 189, TIME_TRAVEL_GB: 189, FAILSAFE_GB: 56, TOTAL_GB: 434 },
      { DATABASE_NAME: 'REPORTING', SCHEMA_NAME: 'PUBLIC', TABLE_NAME: 'DAILY_METRICS', ACTIVE_GB: 123, TIME_TRAVEL_GB: 234, FAILSAFE_GB: 45, TOTAL_GB: 402 },
    ],
    time_travel_hogs: [
      { DATABASE_NAME: 'REPORTING', SCHEMA_NAME: 'PUBLIC', TABLE_NAME: 'DAILY_METRICS', ACTIVE_GB: 123, TIME_TRAVEL_GB: 234, FAILSAFE_GB: 45 },
      { DATABASE_NAME: 'ML', SCHEMA_NAME: 'FEATURES', TABLE_NAME: 'TRAINING_DATA', ACTIVE_GB: 189, TIME_TRAVEL_GB: 189, FAILSAFE_GB: 56 },
    ],
    unused_tables: [
      { DATABASE_NAME: 'LEGACY', SCHEMA_NAME: 'OLD', TABLE_NAME: 'CUSTOMERS_2024', SIZE_GB: 45, LAST_ACCESSED: null },
      { DATABASE_NAME: 'DEV', SCHEMA_NAME: 'TEST', TABLE_NAME: 'TEST_DATA_BACKUP', SIZE_GB: 23, LAST_ACCESSED: '2025-10-15' },
      { DATABASE_NAME: 'STAGING', SCHEMA_NAME: 'TEMP', TABLE_NAME: 'IMPORT_20250101', SIZE_GB: 12, LAST_ACCESSED: '2025-01-02' },
    ],
    database_breakdown: [
      { DATABASE_NAME: 'ANALYTICS', AVG_DATABASE_GB: 890, AVG_FAILSAFE_GB: 212 },
      { DATABASE_NAME: 'RAW', AVG_DATABASE_GB: 567, AVG_FAILSAFE_GB: 134 },
      { DATABASE_NAME: 'REPORTING', AVG_DATABASE_GB: 345, AVG_FAILSAFE_GB: 89 },
      { DATABASE_NAME: 'ML', AVG_DATABASE_GB: 234, AVG_FAILSAFE_GB: 67 },
      { DATABASE_NAME: 'DEV', AVG_DATABASE_GB: 123, AVG_FAILSAFE_GB: 23 },
    ],
    potential_savings: {
      time_travel_reduction: 2400,
      unused_table_cleanup: 1840,
      clone_cleanup: 450,
      total_monthly_savings: 4690
    },
    findings: [
      {
        category: "time_travel",
        severity: "medium",
        title: "Excessive Time Travel Storage",
        description: "2 tables have Time Travel storage exceeding their active data",
        potential_savings: "$2,400.00/month",
        action: "Consider reducing TIME_TRAVEL_DAYS from 7 to 1 for non-critical tables"
      },
      {
        category: "unused",
        severity: "high",
        title: "Potentially Unused Tables",
        description: "3 tables haven't been accessed in 90+ days",
        potential_savings: "$1,840.00/month",
        action: "Review and archive or drop unused tables"
      },
      {
        category: "clones",
        severity: "low",
        title: "Clone Storage",
        description: "19.5 GB retained for clones",
        potential_savings: "$450.00/month",
        action: "Drop unused clones to reclaim storage"
      }
    ]
  });

  useEffect(() => {
    fetchData();
  }, [isConnected]);

  const handleConnected = (creds) => {
    updateCredentials(creds);
    setShowConnect(false);
  };

  const displayData = storageData || (shouldShowData ? getMockData() : null);
  
  if (!displayData) {
    return (
      <div className="space-y-6" data-testid="storage-detective">
        <EmptyDataState title="Storage Detective" description="Connect your Snowflake account to analyze storage usage and find optimization opportunities." />
      </div>
    );
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800';
      default: return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
    }
  };

  return (
    <div className="space-y-6" data-testid="storage-detective">
      <SnowflakeConnect 
        isOpen={showConnect} 
        onClose={() => setShowConnect(false)}
        onConnected={handleConnected}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/snowflake">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
              <Search className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Storage Detective</BrandTitle>
            </h1>
            <p className="text-slate-700 text-sm">Find hidden storage costs and optimize</p>
          </div>
        </div>
        <Button onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Analyzing...' : 'Run Analysis'}
        </Button>
      </div>

      {/* Savings Banner */}
      <div className="bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold mb-1">Potential Monthly Savings Found</h3>
            <p className="opacity-90">
              Optimize storage to reduce your bill by analyzing Time Travel, unused tables, and clones.
            </p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold">${displayData.potential_savings.total_monthly_savings.toLocaleString()}</p>
            <p className="opacity-90">per month</p>
          </div>
        </div>
      </div>

      {/* Current Storage Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Storage</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayData.current_storage.total_tb.toFixed(2)} TB</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Time Travel + Fail-safe</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayData.current_storage.failsafe_tb.toFixed(2)} TB</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <Database className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Stage Storage</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayData.current_storage.stage_tb.toFixed(2)} TB</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Storage Trend */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Storage Growth Trend</CardTitle>
          <CardDescription>Total storage over the last month</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
              <AreaChart data={displayData.storage_trend}>
                <defs>
                  <linearGradient id="storageGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="USAGE_DATE" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                  formatter={(value) => [`${value.toFixed(2)} TB`, 'Storage']}
                />
                <Area type="monotone" dataKey="STORAGE_TB" stroke="#06b6d4" strokeWidth={2} fill="url(#storageGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Findings */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Key Findings
          </CardTitle>
          <CardDescription>Issues detected and recommended actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {displayData.findings.map((finding, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(finding.severity)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {finding.category === 'time_travel' && <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                    {finding.category === 'unused' && <Trash2 className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                    {finding.category === 'clones' && <Archive className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{finding.title}</h4>
                        <Badge className={getSeverityColor(finding.severity)}>{finding.severity.toUpperCase()}</Badge>
                      </div>
                      <p className="text-sm opacity-80 mb-2">{finding.description}</p>
                      <p className="text-sm"><strong>Action:</strong> {finding.action}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-600">{finding.potential_savings}</p>
                    <p className="text-xs opacity-60">savings</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Tables */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-500" />
            Top Tables by Storage
          </CardTitle>
          <CardDescription>Largest tables with storage breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {displayData.top_tables.slice(0, 5).map((table, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {table.DATABASE_NAME}.{table.SCHEMA_NAME}.{table.TABLE_NAME}
                    </p>
                    <p className="text-sm text-slate-400">Total: {table.TOTAL_GB.toLocaleString()} GB</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-20">Active</span>
                    <Progress value={(table.ACTIVE_GB / table.TOTAL_GB) * 100} className="h-2 flex-1 [&>div]:bg-emerald-500" />
                    <span className="text-xs text-slate-400 w-16 text-right">{table.ACTIVE_GB} GB</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-20">Time Travel</span>
                    <Progress value={(table.TIME_TRAVEL_GB / table.TOTAL_GB) * 100} className="h-2 flex-1 [&>div]:bg-amber-500" />
                    <span className="text-xs text-slate-400 w-16 text-right">{table.TIME_TRAVEL_GB} GB</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-20">Fail-safe</span>
                    <Progress value={(table.FAILSAFE_GB / table.TOTAL_GB) * 100} className="h-2 flex-1 [&>div]:bg-red-500" />
                    <span className="text-xs text-slate-400 w-16 text-right">{table.FAILSAFE_GB} GB</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Unused Tables */}
      {displayData.unused_tables.length > 0 && (
        <Card className="border-0 shadow-md border-l-4 border-l-red-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Potentially Unused Tables
            </CardTitle>
            <CardDescription>Tables not accessed in 90+ days - consider archiving</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-400">Table</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-slate-400">Size</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-slate-400">Last Accessed</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-slate-400">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {displayData.unused_tables.map((table, idx) => (
                    <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm">{table.DATABASE_NAME}.{table.SCHEMA_NAME}.{table.TABLE_NAME}</span>
                      </td>
                      <td className="py-3 px-4 text-right">{table.SIZE_GB} GB</td>
                      <td className="py-3 px-4 text-right text-slate-400">
                        {table.LAST_ACCESSED || 'Never accessed'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="outline" size="sm" className="text-red-500 border-red-200">
                          <Eye className="w-3 h-3 mr-1" />
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StorageDetective;
